/**
 * Single entry point for an active chat's task concerns:
 * - loads the long-task  lifecycle map and recovers stranded terminal directives
 *   (`useChatLongTaskLifecycle`)
 * - mutates the tasks cache from arriving tool results (`useToolResultSweep`)
 * - consumes the message handed over through router nav-state by the task list's
 *   "Ask about…" button
 * - drains queued directives into the message list between turns
 *
 * A task row is registered and its status is updated by the tool-result sweep.
 * Renaming a chat thread doesn't require a task write, because the task-list
 * popup joins on the live threads query.
 */
import React from "react";
import { useLocation, useNavigate } from "react-router";

import * as types from "types";
import type { McpConfig } from "types/Mcp";
import type { ChatStreamHandle } from "services/cortevaChain/hooks/useChatStream";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";
import { useTaskWriteInFlight } from "hooks/tasks/useTaskWriteInFlight";
import {
  usePendingInjections,
  planInjectionDrain,
  injectionToMessage,
} from "hooks/tasks/usePendingInjections";
import { useChatLongTaskLifecycle } from "./useChatLongTaskLifecycle";
import { useToolResultSweep } from "./useToolResultSweep";

interface UseChatTasksOptions {
  threadId: string;
  /** The active persona's MCP config, which the long-task tool bindings come from. */
  mcpConfig: McpConfig | undefined;
  getAccessToken: () => Promise<string>;
  messages: types.Message[];
  /** In-memory append, for a message the user is about to send. */
  setMessages: React.Dispatch<React.SetStateAction<types.Message[]>>;
  /** Append that also persists, for a drained directive (see the drain effect). */
  commitMessages: React.Dispatch<React.SetStateAction<types.Message[]>>;
  /**
   * Passed whole rather than destructured by the caller: the drain gates on
   * `status`, `isTurnInFlight`, and `turnsEnded` together (see the drain
   * effect).
   */
  streamState: ChatStreamHandle;
}

interface UseChatTasksResult {
  /**
   * Appends a follow-up user message asking the agent to check a task's
   * status/result/failure. The agent calls the appropriate tool; the sweep picks
   * up the result and updates the task row.
   */
  onAskAboutTask: (prompt: string) => void;
  /**
   * Refetches the tasks list, skipping the request while any task write is in
   * flight: a GET landing mid-write can resolve with pre-commit server state and
   * overwrite a just-registered row or a just-written terminal status.
   */
  refetchTasksUnlessWriting: () => void;
}

export function useChatTasks({
  threadId,
  mcpConfig,
  getAccessToken,
  messages,
  setMessages,
  commitMessages,
  streamState,
}: UseChatTasksOptions): UseChatTasksResult {
  const location = useLocation();
  const navigate = useNavigate();

  const onAskAboutTask = React.useCallback(
    (prompt: string) => {
      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          uuid: crypto.randomUUID(),
          content: prompt,
        },
      ]);
    },
    [setMessages]
  );

  // Loads tool metadata, recovers stranded terminal directives on thread open,
  // and marks terminal completions seen.
  useChatLongTaskLifecycle({
    threadId,
    mcpConfig,
    getAccessToken,
    messages,
    streamStatus: streamState.status,
  });

  // Mutates the tasks cache when a relevant tool result arrives, rather than
  // waiting for the next poll tick.
  useToolResultSweep({ messages, threadId, mcpConfig });

  // Consume the message injected via router nav-state by the task-list "Ask
  // about…" button, deferring while a stream is in flight. Clear the state after
  // consuming so it does not re-fire on re-render or back-navigation.
  const injectMessage = (location.state as { injectMessage?: string } | null)
    ?.injectMessage;

  React.useEffect(() => {
    if (!injectMessage) return;
    if (streamState.status === "loading") return;
    onAskAboutTask(injectMessage);
    navigate(location.pathname, { replace: true, state: null });
  }, [
    injectMessage,
    streamState.status,
    onAskAboutTask,
    navigate,
    location.pathname,
  ]);

  // Drain queued directives into the message list when no stream is in flight,
  // in the order peek → append+persist → acknowledge.
  //
  // Uses commitMessages (not setMessages) so the directive is persisted
  // immediately, making it work across devices.
  const {
    byChat: pendingByChat,
    peek: peekPendingInjections,
    acknowledge: acknowledgePendingInjections,
  } = usePendingInjections();
  const hasPending = (pendingByChat[threadId]?.length ?? 0) > 0;
  // Ids of injections already committed by this mount, needed by StrictMode's
  // second effect invocation. Not redundant with `planInjectionDrain`'s
  // message-list scan, which ignores the user-initiated kinds (so a deliberate
  // repeat click is honored); for those this is the only dedup.
  const drainedIdsRef = React.useRef<Set<string>>(new Set());
  // Reads the committed message list for the dedup pass below without depending
  // on it: re-running per streamed token would thrash the drain cycle.
  const readMessages = React.useEffectEvent(() => messages);
  // Destructured so the drain effect depends on these members rather than the
  // whole `streamState` object, which is recreated every render.
  const { isTurnInFlight, turnsEnded, status: streamStatus } = streamState;
  React.useEffect(() => {
    // One directive per turn. `isTurnInFlight()` makes this exact: acknowledging
    // a directive rewrites the queue atom and re-runs this effect
    // on the next commit, while `useChatStream` registers its request
    // synchronously but dispatches `loading` only after `await getAccessToken()`.
    if (streamStatus === "loading") return;
    if (isTurnInFlight()) return;
    if (!hasPending) return;
    if (injectMessage) return;

    const peeked = peekPendingInjections(threadId);
    if (peeked.length === 0) return;

    // Drops injections already committed by a peer, then selects at most one
    // fresh injection for this turn.
    const { duplicateIds, next } = planInjectionDrain(peeked, readMessages());
    if (duplicateIds.length > 0) {
      acknowledgePendingInjections(threadId, duplicateIds);
    }
    if (!next) return;
    if (drainedIdsRef.current.has(next.id)) return;

    // No try/catch: commitMessages writes the cache synchronously and sends the
    // PUT as a fire-and-forget mutation, so a failed write does not throw here.
    // It surfaces as a MessagesWriteError from a later render of
    // useCurrentMessages, recovered by its error boundary resending the
    // already-cached list.
    const message = injectionToMessage(next);
    commitMessages((prev) => [...prev, message]);
    drainedIdsRef.current.add(next.id);
    acknowledgePendingInjections(threadId, [next.id]);
  }, [
    threadId,
    streamStatus,
    // Every ended turn bumps this, so the drain re-runs even when the status string
    // does not change (a second `error` after an `error`).
    turnsEnded,
    isTurnInFlight,
    hasPending,
    peekPendingInjections,
    acknowledgePendingInjections,
    commitMessages,
    injectMessage,
  ]);

  const { refetch: refetchTasks } = useTasksQuery();
  const taskWriteInFlight = useTaskWriteInFlight();
  const refetchTasksUnlessWriting = React.useCallback(() => {
    if (taskWriteInFlight) return;
    refetchTasks();
  }, [taskWriteInFlight, refetchTasks]);

  return { onAskAboutTask, refetchTasksUnlessWriting };
}
